/**
 * Server-Sent Events (SSE) Utility
 * Handles streaming communication analysis from backend
 */

export interface SSEEvent {
  status: string;
  message?: string;
  result?: any;
  error?: string;
}

export type SSECallback = (event: SSEEvent) => void;

export interface SSEConnectionOptions {
  url: string;
  method?: 'GET' | 'POST';
  body?: any;
  headers?: Record<string, string>;
  onEvent: SSECallback;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

/**
 * Create SSE connection using fetch API
 * More reliable than EventSource for POST requests
 */
export const createSSEConnection = async ({
  url,
  method = 'POST',
  body,
  headers = {},
  onEvent,
  onError,
  onComplete,
}: SSEConnectionOptions): Promise<() => void> => {
  const controller = new AbortController();
  const signal = controller.signal;

  try {
    const response = await fetch(url, {
      method,
      headers: {
        'Accept': 'text/event-stream',
        'Content-Type': 'application/json',
        ...headers, // Include CSRF token and other custom headers
      },
      body: method === 'POST' ? JSON.stringify(body) : undefined,
      credentials: 'include', // Important: Send cookies (includes session)
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // Read stream
    const processStream = async () => {
      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            if (onComplete) onComplete();
            break;
          }

          // Decode chunk
          buffer += decoder.decode(value, { stream: true });

          // Process complete messages (separated by \n\n)
          const messages = buffer.split('\n\n');
          buffer = messages.pop() || ''; // Keep incomplete message in buffer

          for (const message of messages) {
            if (!message.trim()) continue;

            // Parse SSE message
            const lines = message.split('\n');
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.substring(6); // Remove "data: " prefix
                try {
                  const event: SSEEvent = JSON.parse(data);
                  onEvent(event);

                  // Handle completion
                  if (event.status === 'complete' || event.status === 'error') {
                    if (onComplete) onComplete();
                    controller.abort();
                    return;
                  }
                } catch (e) {
                  console.error('Failed to parse SSE event:', e, 'Data:', data);
                }
              }
            }
          }
        }
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error('Stream processing error:', err);
          if (onError) onError(err);
        }
      }
    };

    // Start processing stream
    processStream();

    // Return abort function
    return () => {
      controller.abort();
    };
  } catch (error) {
    console.error('SSE connection error:', error);
    if (onError && error instanceof Error) {
      onError(error);
    }
    throw error;
  }
};

/**
 * Analysis progress steps
 */
export const ANALYSIS_STEPS = [
  { status: 'processing_activity', label: 'Processing communication', icon: 'psychology' },
  { status: 'activity_processed', label: 'Activity processed', icon: 'check_circle' },
  { status: 'getting_traits', label: 'Analyzing behavioral traits', icon: 'person_search' },
  { status: 'traits_received', label: 'Traits identified', icon: 'check_circle' },
  { status: 'analyzing_clarity', label: 'Evaluating clarity', icon: 'visibility' },
  { status: 'clarity_analyzed', label: 'Clarity analyzed', icon: 'check_circle' },
  { status: 'getting_explanation', label: 'Generating AI insights', icon: 'auto_awesome' },
  { status: 'explanation_received', label: 'Insights generated', icon: 'check_circle' },
  { status: 'complete', label: 'Analysis complete', icon: 'task_alt' },
] as const;

export const getStepInfo = (status: string) => {
  return ANALYSIS_STEPS.find(step => step.status === status) || {
    status,
    label: 'Processing...',
    icon: 'pending'
  };
};

